import { ComponentBase } from "@abstracts/component-base";
import {
  AfterViewInit,
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Blackbox } from "@models/blackbox";
import { EnergyMeter } from "@models/energyMeter";
import { EnergyMonitorData } from "@dto/energyMonitorData";
import { NgbDate, NgbDatepicker } from "@ng-bootstrap/ng-bootstrap";
import { BlackboxService } from "@services/entity/blackbox.service";
import { EnergyMeterRecordsService } from "@services/entity/energyMeterRecordsService";
import { ToastService } from "@services/toast/toast.service";
import { Chart, registerables } from "chart.js";
import { plainToClass } from "class-transformer";
import { KwhPipe } from "../../../pipes/kwh.pipe";
import { EnergyMonitorHistoryData } from "../../../dto/energyMonitorHistoryData";

Chart.register(...registerables);

@Component({
  selector: "app-energy-monitor",
  templateUrl: "./energy-monitor.component.html",
  styleUrls: ["./energy-monitor.component.scss"],
})
export class EnergyMonitorComponent
  extends ComponentBase
  implements OnInit, OnDestroy
{
 
  public className: string;

  public blackbox: Blackbox[];
  public data: EnergyMonitorData = new EnergyMonitorData();
  public groupDataByType: Record<string, EnergyMonitorHistoryData[]> = {};
  public energyMeterTypeKeys = Object.keys;

  public today: Date = new Date();
  public dateSelected: Date;
  public loadingDistribution: boolean = true;
  public loadingHistory: boolean = true;
  public isOperatorView: boolean;
  public customerId: string;

  @ViewChild("datepicker") datePicker: NgbDatepicker;

  public interval: any;

  constructor(
    public toastService: ToastService,
    public energyMeterRecordsService: EnergyMeterRecordsService,
    public blackboxService: BlackboxService,
    private activatedRoute: ActivatedRoute,
    injector: Injector
  ) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.isOperatorView = this.activatedRoute.snapshot.data["isOperatorView"];
    if (this.isOperatorView)
      this.customerId = this.activatedRoute.parent.snapshot.params.key;
    else this.customerId = localStorage.getItem("customerId") || undefined;

    this.setDate();
    this.getData();
  }

  setDate(): void {
    const query = new URLSearchParams(document.location.search).get("d");

    if (query) {
      const target = new Date(query);
      this.dateSelected = new Date(
        target.getFullYear(),
        target.getMonth(),
        target.getDate(),
        0,
        0,
        0,
        0
      );
      return;
    }

    const now = new Date();
    this.dateSelected = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0
    );
  }

  getData(): void {
    this.getBlackbox();
  }

  startInterval(): void {
    this.interval = setInterval(() => this.getData(), 10000);
  }

  getBlackbox() {
    if (this.customerId == undefined) {
      this.toastService.warn(
        $localize`:@@EnergyMonitorCustomerNoBlackbox:Der Kunde ist kein Nutzer von CUBOS.Energy.`
      );
      return;
    }

    this.blackboxService
      .getByCustomerId(parseInt(this.customerId))
      .subscribe((result) => {
        this.blackbox = plainToClass(Blackbox, result);
        this.getEnergyMonitorData();
      });
  }

  getEnergyMonitorData() {
    const request = {
      utcStartDate: this.dateSelected ?? null,
      utcEndDate: new Date(this.dateSelected.getTime() + 86400000),
      blackboxId: this.blackbox[0]?.id,
      maxResult: 360,
    };

    this.energyMeterRecordsService.getEnergyMonitorData(request).subscribe(
      (data) => {
        this.data = data;
        this.loadingDistribution = false;
        this.loadingHistory = false;
        this.groupDataByType = this.groupByEnergyMeterTypeName(this.data);
      },
      (error) => {
        this.groupDataByType = {};
        this.data = new EnergyMonitorData();
        this.loadingDistribution = false;
        this.loadingHistory = false;
      }
    );
  }

  groupByEnergyMeterTypeName(
    data: EnergyMonitorData
  ): Record<string, EnergyMonitorHistoryData[]> {
    return data.historyData.reduce((acc, history) => {
      const typeName = history.energyMeter.energyMeterType.name;
      if (!acc[typeName]) {
        acc[typeName] = [];
      }
      acc[typeName].push(history);
      return acc;
    }, {} as Record<string, EnergyMonitorHistoryData[]>);
  }

  onDateSelection(date: NgbDate, drop: any) {
    this.loadingDistribution = true;
    this.loadingHistory = true;
    this.dateSelected = new Date(
      date.year,
      date.month - 1,
      date.day,
      0,
      0,
      0,
      0
    );
    this.getEnergyMonitorData();
    drop.close();
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
  }

  async download() {
    this.toastService.loading(
      $localize`:@@DatasetDownload:Datensatz wird heruntergeladen. Das kann einen Moment dauern.`
    );
    this.blackboxService.downloadCsv(this.blackbox[0].id, this.dateSelected.getFullYear()).subscribe(
      (response: any) => {
        const file = new Blob([response], { type: "text/csv" });
        const fileUrl = URL.createObjectURL(file);

        let fileName = `${this.getPrefix(
          this.dateSelected
        )}__${this.dateSelected.getFullYear()}_Energy_Monitor.csv`;
        var a = document.createElement("a");
        document.body.appendChild(a);
        a.style.display = "none";
        a.href = fileUrl;
        a.download = fileName;
        a.click();

        window.URL.revokeObjectURL(fileUrl);
        this.toastService.success(
          $localize`:@@DatasetDownloadSuccess:Datensatz erfolgreich heruntergeladen.`,
          0,
          true
        );
      },
      (error) => {
        console.error(error);
        this.toastService.error(
          $localize`:@@DatasetDownloadError:Datensatz konnte nicht heruntergeladen werden.`,
          0,
          true
        );
      }
    );
  }

  async downloadX() {
    this.toastService.loading(
      $localize`:@@DatasetDownload:Datensatz wird heruntergeladen. Das kann mehrere Minuten dauern.`
    );
    this.blackboxService.downloadXlsx(this.blackbox[0].id, this.dateSelected.getFullYear()).subscribe(
      (response: any) => {
        const file = new Blob([response], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const fileUrl = URL.createObjectURL(file);

        let fileName = `${this.dateSelected.getFullYear()}_Energy_Monitor.xlsx`;
        var a = document.createElement("a");
        document.body.appendChild(a);
        a.style.display = "none";
        a.href = fileUrl;
        a.download = fileName;
        a.click();

        window.URL.revokeObjectURL(fileUrl);
        this.toastService.success(
          $localize`:@@DatasetDownloadSuccess:Datensatz erfolgreich heruntergeladen.`,
          0,
          true
        );
      },
      (error) => {
        console.error(error);
        this.toastService.error(
          $localize`:@@DatasetDownloadError:Datensatz konnte nicht heruntergeladen werden.`,
          0,
          true
        );
      }
    );
  }

  getPrefix(date: Date) {
    const offset = date.getTimezoneOffset();
    date = new Date(date.getTime() - offset * 60 * 1000);
    return date.toISOString().split("T")[0];
  }

  convertToPercentage(value: number) {
    return Math.round(value * 100) / 100;
  }

  convertToAbsolute(value: number) {
    return Math.abs(value);
  }

  calculateTotalEnergy(key: string): number {
    return this.convertToAbsolute(
      this.groupDataByType[key].reduce(
        (total, dataset) => total + dataset.energy,
        0
      )
    );
  }

  calculateTotalEnergyNeg(key: string): number {
    return this.convertToAbsolute(
      this.groupDataByType[key].reduce(
        (total, dataset) => total + dataset.energyNeg,
        0
      )
    );
  }

  calculateTotalEnergyPos(key: string): number {
    return this.convertToAbsolute(
      this.groupDataByType[key].reduce(
        (total, dataset) => total + dataset.energyPos,
        0
      )
    );
  }
}
